import { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Toolbar,
  Divider,
  Button,
  IconButton,
  Paper,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import { useApi } from '@/providers/ApiContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useLocation } from 'react-router';
import { LoadingButton } from '@mui/lab';
import { getPlot, updatePlot, createPlot } from '@/actions/plot';
import { getChart } from '@/actions/chart';
import PlotNameField from './PlotNameField';
import PlotChartField from './PlotChartField';

// import { countTokens } from '@/actions/tokenize';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import PLOT_STATUSES from '../../../plotStatuses';
import PlotRecordIDField from './PlotRecordIDField';
import type { Plot, Chart, EntityId } from '@/types';
// import { insertPlotAsset } from '../../../../actions/plotAsset';

interface PlotFormProps {
  type?: string;
}

function PlotForm({ type }: PlotFormProps): React.JSX.Element {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const doc_types = useDocTypes();
  const [plot, setPlot] = useState<Plot | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pristine, setPristine] = useState<boolean>(true);
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [record_id, setRecordId] = useState<string>('');
  const [record_id_error, setRecordIdError] = useState<string | boolean>(false);
  const [chart, setChart] = useState<Chart | null>(null);
  const [chart_error, setChartError] = useState<string | boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const sp = new URLSearchParams(location.search);
  const plot_id = sp.get('plot_id');

  const base_path = '/cartographer/plot';

  // Get the document type with code 'dhf' for chart selection
  const dhf_document_type = doc_types?.find(
    (doc_type) => doc_type.code === 'dhf',
  );

  useEffect(() => {
    if (user?.entity_id && !plot && api) {
      if (plot_id) {
        getPlot(api, plot_id as EntityId)
          .then(async (data) => {
            setPlot(data);
            setName(data?.name || '');
            setRecordId(data?.record_id || '');
            // Set chart if chart_id exists
            if (data?.chart_id) {
              try {
                if (!api) return;
                const chartData = await getChart(api, data.chart_id);
                setChart(chartData);
              } catch (error) {
                console.error('Error fetching chart:', error);
                // Set a fallback object if chart fetch fails
                setChart({
                  entity_id: data.chart_id,
                  name: 'Chart not found',
                } as Chart);
              }
            }
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load plot for editing',
                errorDetails:
                  error instanceof Error
                    ? error.message
                    : 'Unable to retrieve the plot for editing. This could be due to network issues, server problems, or the plot may no longer exist.',
                returnPath: base_path,
              },
            });
          });
      }
    }
  }, [user?.entity_id, plot_id, plot, api, navigate, type]);

  const handleSubmit = async () => {
    if (!api) return;
    try {
      setLoading(true);

      const payload = {
        name,
        record_id,
        chart_id: chart?.entity_id,
      };

      let updated_plot: Plot;
      if (plot_id && plot) {
        updated_plot = await updatePlot(
          api,
          plot.entity_id,
          plot.status === PLOT_STATUSES.DRAFT ? payload : { name },
        );
      } else {
        updated_plot = await createPlot(api, {
          type: type || '',
          ...payload,
        });
      }
      navigate(`${base_path}/${updated_plot.entity_id}`);
    } catch (error) {
      console.error('Error saving plot:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to save the plot. This could be due to network issues, server problems, or invalid data.';
      setApiError(errorMessage);
      setLoading(false);
    }
  };

  if (!user?.entity_id || (!plot && plot_id)) {
    return <LoadingMask />;
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <IconButton sx={{ mr: 2 }} onClick={() => navigate(-1)}>
              <ArrowBackOutlinedIcon />
            </IconButton>
            <Typography variant='h6' sx={{ mr: 4 }}>
              {plot ? 'Edit Plot' : 'New Plot'}
            </Typography>
          </Toolbar>
        </Paper>
      </Grid>
      <Divider />
      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2} style={{ padding: '15px' }}>
            <Grid size={12}>
              <PlotNameField
                value={name}
                setValue={setName}
                error={name_error}
                setError={setNameError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
            <Grid size={12}>
              <PlotRecordIDField
                value={record_id}
                setValue={setRecordId}
                error={record_id_error}
                setError={setRecordIdError}
                setChanged={() => setPristine(false)}
              />
            </Grid>
            <Grid size={12}>
              <PlotChartField
                document_type_id={dhf_document_type?.entity_id}
                value={chart}
                setValue={setChart}
                error={chart_error}
                setError={setChartError}
                setChanged={() => setPristine(false)}
                edit={plot_id || undefined}
              />
            </Grid>
          </Grid>
        </Box>

        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Toolbar>
            <Stack spacing={2} direction='row'>
              <LoadingButton
                variant='contained'
                onClick={handleSubmit}
                loading={loading}
                disabled={
                  plot_id
                    ? pristine ||
                      Boolean(name_error) ||
                      !Boolean(name) ||
                      Boolean(record_id_error) ||
                      !Boolean(record_id) ||
                      Boolean(chart_error) ||
                      !Boolean(chart)
                    : pristine ||
                      Boolean(name_error) ||
                      !Boolean(name) ||
                      Boolean(record_id_error) ||
                      !Boolean(record_id) ||
                      Boolean(chart_error) ||
                      !Boolean(chart)
                }
              >
                Submit
              </LoadingButton>

              <Button
                variant='outlined'
                color='inherit'
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </Button>
            </Stack>
          </Toolbar>
        </Grid>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Grid>
  );
}

export default PlotForm;
